// Types based on backend models
import Permission from "../util/access/Permission";

export interface ClientConfig {
    googleClientID: string;
    webSocketUrl: string;
    forceRefreshVersion: number;
}

export interface KeyVaultRef {
    id: number;
    name: string;
    color: string;
}

export interface KeyTag {
    id: number;
    name: string;
    description: string | null;
    color: string;
}

export interface KeyTagForm {
    name: string;
    description: string | null;
    color: string;
}

export interface EmployeeRef {
    id: number;
    fullName: string;
    deactivated: boolean;
}


export type KeyLocation = {
    type: 'with-vault';
} | {
    type: 'with-employee';
    employee: EmployeeRef;
    expTo?: string;
} | {
    type: 'with-guest';
    fullName: string;
    company?: string;
    expTo?: string;
} | {
    type: 'lost';
} | {
    type: 'damaged';
} | {
    type: 'deactivated';
};

export interface KeyTransaction {
    keyNumber: string,
    vault: KeyVaultRef,
    date: string,
    newLocation: KeyLocation,
    notes: string,
}


export interface KeyHistoryRecord {
    id: number;
    from?: string;
    to?: string;
    vault: KeyVaultRef;
    location: KeyLocation;
    notes?: string;
    createdBy?: string | null;
    createdAt?: string | null;
    updatedBy?: string | null;
    updatedAt?: string | null;
}

export interface KeyData {
    id: number;
    keyNumber: string;
    transponderId?: string | null;
    tags: KeyTag[];
    history: KeyHistoryRecord[];
}

export interface KeyHistoryRecordForm {
    from?: string;
    to?: string;
    vault: KeyVaultRef;
    location: KeyLocation;
    notes?: string;
}

export interface CreateKeyHistoryRecordForm extends KeyHistoryRecordForm {
    finishPreviousRecords: boolean
}

export interface KeyRange {
    id: number;
    start?: number;
    end?: number;
}

export interface BulkAddKeysForm {
    keyRanges: KeyRange[];
    record: KeyHistoryRecordForm;
}

// Form data interfaces
export interface KeyDataForm {
    keyNumber: string;
    transponderId?: string | null;
    tags: KeyTag[];
}

export const BUILTIN_COLORS = {
    DEFAULT: 'default',
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
};


export interface KeyVault {
    id: number;
    name: string;
    color: string;
}

export interface KeyVaultForm {
    name: string;
    color: string;
}

export interface GlobalKeyVault extends KeyVault {
    officeId: number;
}

export type KeySnapshot = {
    key: KeyData,
    historyRecord: KeyHistoryRecord,
};

export interface WorkSite {
    id: number;
    name: string;
    badge?: string;
    emoji?: string;
    color?: string;
    archived: boolean;
}

export interface WorkSiteForm {
    badge?: string;
    emoji?: string;
    color?: string;
}

export interface HibobOffice {
    id: string;
    name: string;
    archived: boolean;
    workSiteId: WorkSite['id'] | null;
    badge?: string;
    color?: string;
}

export interface HibobOfficeForm {
    workSiteId: WorkSite['id'] | null;
    badge?: string;
    color?: string;
}

export interface Employee extends EmployeeRef {
    id: number,
    fullName: string,
    email: string,
    siteId?: number | null,
    hibobOfficeId?: string | null,
    hibobId: string,
    hrEmployeeId?: string | null,
    managerHibobId?: string | null,
    department?: string | null,
    teamId?: string | null,
    startDate?: string | null,
    jobTitle?: string | null,
    wearSize?: string | null,
    shirtStyle?: string | null,
    deactivated: boolean,
}

export type GuestRole = { type: 'guest'; }
export type SystemAdminRole = { type: 'system-admin'; }
export type OfficeAdminRole = {
    type: 'office-admin';
    officeId: number;
}
export type VaultManagerRole = {
    type: 'vault-manager';
    officeId: number;
    vaultId: number;
}
export type Role = GuestRole | SystemAdminRole | OfficeAdminRole | VaultManagerRole;

export interface UserRole {
    id: number;
    email: string;
    role: Role;
}

export interface UserRoleForm {
    email: string;
    role: Role;
}

export interface PermanentToken {
    id: number;
    name: string;
    description?: string | null;
    officeId?: number | null;
    vaultId?: number | null;
    permissions: Permission[];
}

export interface PermanentTokenForm {
    name: string;
    description?: string | null;
    officeId?: number | null;
    vaultId?: number | null;
    permissions: Permission[];
}

export interface NewPermanentToken {
    permanentToken: PermanentToken;
    token: string;
}

export interface User {
    id: string;
    name: string;
    email: string;
    roles: Role[];
    picture?: string;
}

export interface KeyOfficeVisitReport {
    id: number;
    uploaded: string;
    uploadedByEmail: string;
    from: string;
    to: string;
    sheet: OfficeVisitSheet<string>;
}

export interface OfficeVisitSheetRow<T, Visit = boolean> {
    value: T;
    visits: Visit[],
}

export interface OfficeVisitSheet<Row, Visit = boolean> {
    dates: string[];
    rows: OfficeVisitSheetRow<Row, Visit>[];
    length: number;
}

export interface Team {
    id: string;
    name: string;
    parentId?: string | null;
    archived: boolean;
}

export interface HibobResidence {
    workSite: WorkSite | null;
    hibobOffice: HibobOffice | null;
}

export interface Office {
    id: number;
    readonly keyOfficeId: number;
    name: string;
    disabled: boolean;
    timezone: string;
    addressLineOne?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    hibobResidence: HibobResidence[];
    checkInsIntegrationEnabled: boolean;
    releezmeIntegrationEnabled: boolean;
}

export interface OfficeForm {
    hibobResidence: HibobResidence[];
    checkInsIntegrationEnabled: boolean;
    releezmeIntegrationEnabled: boolean;
}

export interface OfficeIntegrationForm {
    checkInsIntegrationEnabled: boolean;
}

export interface Neighbourhood {
    type: 'existing';
    id: string;
    key: string;
    name: string;
    displayColor?: string | null;
    notes?: string | null;
    slackChannelId?: string | null;
    employees: EmployeeRef[];
    desks: DeskRef[];
}

export const UNASSIGNED_NEIGHBOURHOOD_ID = 'unassigned';
export const UNASSIGNED_NEIGHBOURHOOD_KEY = `existing-${UNASSIGNED_NEIGHBOURHOOD_ID}`;

export function isAssigned(neighbourhood: Neighbourhood): boolean {
    return neighbourhood.id !== UNASSIGNED_NEIGHBOURHOOD_ID;
}

export interface NeighbourhoodForm {
    name: string;
    displayColor?: string;
    notes?: string;
    slackChannelId?: string;
}

export interface DeskRef {
    id: string;
    name: string;
}

export interface Desk {
    id: string;
    name: string;
    assignedTo?: EmployeeRef | null;
    neighbourhoodId?: string | null;
    floorId?: string | null;
    xPos?: number | null;
    yPos?: number | null;
    roomName: string;
}

export interface Floor {
    id: string;
    name: string;
    floorPlanUrl?: string | null;
}

export interface OfficeVisit {
    date: string;
    visitorCount: number;
    type: 'work-day' | 'weekend';
}

export interface SeatingPlanNewNeighbourhood {
    type: 'new';
    id: number;
    key: string;
    name: string;
    displayColor?: string | null;
    notes?: string | null;
    slackChannelId?: string | null;
}

export interface SeatingPlanNewNeighbourhoodForm {
    name: string;
    displayColor?: string | null;
    notes?: string | null;
    slackChannelId?: string | null;
}

export interface SeatingPlanNeighbourhoodUpdate {
    neighbourhoodId: string;
    name: string;
    displayColor?: string | null;
    notes?: string | null;
    slackChannelId?: string | null;
}

export type INeighbourhood = SeatingPlanNewNeighbourhood | Neighbourhood;

export type SeatingPlanNewNeighbourhoodRef = {
    type: 'new',
    id: number,
};

export type SeatingPlanExistingNeighbourhoodRef = {
    type: 'existing',
    id: string,
};

export type SeatingPlanNeighbourhoodRef = SeatingPlanExistingNeighbourhoodRef | SeatingPlanNewNeighbourhoodRef;

export interface SeatingPlanMove {
    employee: EmployeeRef;
    neighbourhood: SeatingPlanNeighbourhoodRef;
    operation: 'add' | 'remove';
}

export interface SeatingPlanDeskAssignment {
    desk: DeskRef;
    neighbourhood?: SeatingPlanNeighbourhoodRef | null;
    employee?: EmployeeRef | null;
}

export interface SeatingPlan {
    id: number,
    name: string,
    description: string | null,
    createdBy: string,
    createdAt: string,
    updatedBy: string,
    updatedAt: string,
    newNeighbourhoods: SeatingPlanNewNeighbourhood[],
    neighbourhoodUpdates: SeatingPlanNeighbourhoodUpdate[],
    moves: SeatingPlanMove[],
    deskAssignments: SeatingPlanDeskAssignment[],
}

export interface SeatingPlanForm {
    name: string,
    description: string | null,
}

export interface NeighbourhoodSnapshot {
    id: number;
    name: string;
    description: string | null;
    createdBy: string;
    createdAt: string;
    neighbourhoodCount: number;
    deskCount: number;
    employeeCount: number;
}

export interface NeighbourhoodSnapshotForm {
    name: string;
    description: string | null;
}

export interface ProgressAuthMessage {
    token: string;
}

type ProgressMessageTypes = 'error' | 'success' | 'warning' | 'info';

export interface ProgressMessage {
    message: string,
    type?: ProgressMessageTypes,
    progress?: number,
}

export type ProblemType =
    | 'employee-with-several-keys'
    | 'not-returned-keys'
    | 'lost-keys'
    | 'damaged-keys'
    | 'keys-with-multiple-active-records'
    ;

export interface SlackNotificationSettings {
    id: number;
    name: string;
    channelId: string;
    cron: string;
    problemTypes: ProblemType[];
    enabled: boolean;
    lastRunAt: number | null;
}

export interface SlackNotificationSettingsForm {
    name: string;
    channelId: string;
    cron: string;
    problemTypes: ProblemType[];
    enabled: boolean;
}

export interface SlackOrganization {
    id: string;
    name: string;
    url: string;
}

export enum EntityType {
    Employees = 'Employees',
    Offices = 'Offices',
    UserRoles = 'UserRoles',
    PermanentTokens = 'PermanentTokens',
    Teams = 'Teams',
    WorkSites = 'WorkSites',
    Desks = 'Desks',
    Floors = 'Floors',
    KeyData = 'KeyData',
    SeatingPlans = 'SeatingPlans',
    NeighbourhoodSnapshots = 'NeighbourhoodSnapshots',
    KeyTags = 'KeyTags',
    VisitReports = 'VisitReports',
    Neighbourhoods = 'Neighbourhoods',
    KeyVaults = 'KeyVaults',
    GlobalKeyVaults = 'GlobalKeyVaults',
    SlackSettings = 'SlackSettings',
    NewJoiners = 'NewJoiners',
    NewJoinerChecklistItems = 'NewJoinerChecklistItems',
}

export enum NewJoinerType {
    NewFTE = 'NewFTE',
    NewIntern = 'NewIntern',
    InternToFTE = 'InternToFTE',
    ReturnFromParentalLeave = 'ReturnFromParentalLeave',
    InternationalRelocation = 'InternationalRelocation',
    LocalRelocation = 'LocalRelocation',
}

export type NewJoinerEmployee =
    | { type: 'existing', employee: EmployeeRef }
    | { type: 'new', fullName: string, manager?: EmployeeRef | null };

export type NewJoinerChecklist = Record<number, boolean>;

export enum ChecklistItemCategory {
    Neighbourhood = 'Neighbourhood',
    Key = 'Key',
    WelcomePack = 'WelcomePack',
    Standalone = 'Standalone',
}

export enum NewJoinerChecklistItemFilter {
    Remote = 'Remote',
    Office = 'Office',
    NewFTE = 'NewFTE',
    NewIntern = 'NewIntern',
    InternToFTE = 'InternToFTE',
    ReturnFromParentalLeave = 'ReturnFromParentalLeave',
    InternationalRelocation = 'InternationalRelocation',
    LocalRelocation = 'LocalRelocation',
}

export interface NewJoinerChecklistItem {
    id: number;
    category: ChecklistItemCategory;
    label: string;
    ordinal: number;
    link?: string | null;
    filters?: NewJoinerChecklistItemFilter[];
}

export interface NewJoinerChecklistItemForm {
    category: ChecklistItemCategory;
    label: string;
    ordinal: number;
    link?: string | null;
    filters?: NewJoinerChecklistItemFilter[];
}

export interface NewJoiner {
    id: number;
    person: NewJoinerEmployee;
    joinDate: string;
    type: NewJoinerType;
    remote: boolean;
    notes?: string | null;
    checklist: NewJoinerChecklist;
}

export interface NewJoinerForm {
    person: NewJoinerEmployee;
    joinDate: string;
    type: NewJoinerType;
    remote: boolean;
    notes?: string | null;
    checklist: NewJoinerChecklist;
}

export interface UpdateSocketClientMessageAuthRequest {
    type: 'auth-request',
    token: string,
}

export interface UpdateSocketClientMessagePing {
    type: 'ping',
    ts: number,
}

export type UpdateSocketClientMessage =
    | UpdateSocketClientMessageAuthRequest
    | UpdateSocketClientMessagePing;

export interface UpdateSocketServerMessageEntityUpdate {
    type: 'entity-update',
    entity: EntityType,
    officeId: number | null,
    etag: string,
}

export interface UpdateSocketServerMessagePong {
    type: 'pong',
    ts: number,
    forceRefreshVersion: number,
}

export type UpdateSocketServerMessage =
    | UpdateSocketServerMessageEntityUpdate
    | UpdateSocketServerMessagePong;
